import { useEffect, useState, useMemo } from "react";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";
import {
  editServiceLine,
  getAvailableQuestionnaires,
  getAvailableResources,
  getServiceLineById,
  presignServiceLineAgentAsset,
} from "../../api/ServiceLine";
import { failed, success } from "../../common/Toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import OrganizationCard from "./OrganizationCard";
import { Col, Row, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import TextArea from "../../common/textfield/TextArea";
import TextInput from "../../common/textfield/TextInput";
import SelectFiled from "../../common/textfield/SelectFiled";
import { getOrganization } from "../../api/Organization";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button } from "../../common/Button";
import MultiSelectV2 from "../../common/textfield/MultiSelectV2";
import MultiSelect from "../../common/textfield/MultiSelect";
import { TableCell, Tooltip, Switch } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { getAuthorizedResources } from "../../api/FillableDocument.js";
import { useSelector } from "react-redux";
import { getReportDeliveries } from "../../api/ReportDeliveries/index.js";
import { getTimeZones } from "@vvo/tzdb";
import Select from "react-select";
import { validation, conditionalValidation } from "../../../utils/constants";
import {
  formatEscalationNumberForDisplay,
  formatEscalationNumberForSubmit,
} from "../../../utils/phoneNumberSupport";
import AgentPromptEditModal from "./AgentPromptEditModal";
import "./organization.css";

const COUNTRY_OPTIONS = [
  { name: "Canada", value: "canada" },
  { name: "United States of America", value: "usa" },
];

async function fetchPresignedText(orgId, serviceLineId, asset, source) {
  const res = await presignServiceLineAgentAsset(orgId, serviceLineId, {
    asset,
    operation: "get",
    source,
  });
  if (!res?.status || !res?.data?.url) {
    throw new Error(res?.message || `Failed to get ${asset}`);
  }
  const response = await fetch(res.data.url);
  if (!response.ok) {
    throw new Error(`Failed to download ${asset} (${response.status})`);
  }
  return { text: await response.text(), key: res.data.key };
}

async function uploadPresignedText(orgId, serviceLineId, asset, text) {
  const res = await presignServiceLineAgentAsset(orgId, serviceLineId, {
    asset,
    operation: "put",
    source: "service-line",
  });
  if (!res?.status || !res?.data?.url || !res?.data?.key) {
    throw new Error(res?.message || `Failed to get upload URL for ${asset}`);
  }
  const putResponse = await fetch(res.data.url, {
    method: "PUT",
    body: text ?? "",
    headers: { "Content-Type": "text/plain" },
  });
  if (!putResponse.ok) {
    throw new Error(`Failed to upload ${asset} (${putResponse.status})`);
  }
  return res.data.key;
}
function EditServiceLine({ serviceLineIdFromParent, orgIdFromParent }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const isAdmin = userRoles?.includes("Super-Admin");

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const serviceLineId =
    state?.serviceLineId ||
    urlParams.get("serviceLineId") ||
    serviceLineIdFromParent;
  const orgId = state?.orgId || urlParams.get("orgId") || orgIdFromParent;
  const [serviceLineData, setServiceLineData] = useState(null);
  const [orgData, setOrgData] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Details");
  const [availableQuestionnaires, setAvailableQuestionnaires] = useState([]);
  const [telephoneIntakeQuestionnaires, setTelephoneIntakeQuestionnaires] =
    useState([]);
  const [avaialableDNIs, setAvailablaDNIs] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [availableReportDeliveries, setAvailableReportDeliveries] = useState(
    []
  );
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const [selectedResources, setSelectedResources] = useState([]);
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);
  const [loadingDefaultRedFlags, setLoadingDefaultRedFlags] = useState(false);
  const [promptModal, setPromptModal] = useState(null); // "systemPrompt" | "redFlags" | null
  const ctiEnabled = orgData?.ctiSettings?.enabled;

  const timeZoneOptions = useMemo(() => {
    const zones = getTimeZones({ includeUtc: true });

    return zones.map((tz) => {
      const now = new Date();

      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz.name,
        timeZoneName: "shortOffset",
      }).formatToParts(now);

      const offset = parts.find((p) => p.type === "timeZoneName")?.value || "";

      const abbrevParts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz.name,
        timeZoneName: "short",
      }).formatToParts(now);

      const abbrev =
        abbrevParts.find((p) => p.type === "timeZoneName")?.value || "";

      const [region = "UTC", city = ""] = (tz.name || "").split("/");
      const formattedRegion = region.replace(/_/g, " ");
      const formattedCity = city.replace(/_/g, " ");
      const countryName = tz.countryName || formattedRegion;

      const label = `${offset} ${countryName}${
        formattedCity ? ` - ${formattedCity}` : ` - ${formattedRegion}`
      }${abbrev ? ` (${abbrev})` : ""}`;

      return {
        value: tz.name,
        label,
        abbrev,
        altName: tz.alternativeName,
      };
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      timeZone: null,
      intakeQuestionnaire: "",
      resources: [""],
      dni: [""],
      reportDeliveries: [""],
      humanEscalationNumber: "",
      systemPrompt: "",
      redFlags: "",
      country: "canada",
      allowPatientCreationWithAI: false,
    },
    validationSchema: Yup.object().shape({
        name: conditionalValidation(
          Yup.string()
            .max(64, "Maximum 64 characters allowed"),
            {
              validation,
              mandatory: true,
              label: "Display Name"
            }),
        description: conditionalValidation( 
            Yup.string()
              .max(128, "Maximum 256 characters allowed"),
              {
                validation,
                label: "Description",
                mandatory: true
              }),
        humanEscalationNumber: Yup.string()
          .trim()
          .matches(
            /^$|^\d{10}$/,
            "Enter a 10-digit phone number or leave blank"
          ),
      }),
    onSubmit: async (values) => {
      values.name = values.name.trim();
      values.description = values.description.trim();

      const formattedResourcesToSave = selectedResources?.map(
        ({ id }) => ({ id })
      );

      const cleanedReportDeliveries = Array.from(
        new Set(
          (values?.reportDeliveries || [])
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v).trim())
            .filter((v) => v !== "")
        )
      );

      setIsPageLoading(true);

      try {
        const existingSettings = serviceLineData?.["agent-settings"] || {};
        const agentSettings = {
          humanEscalationNumber: formatEscalationNumberForSubmit(
            values?.humanEscalationNumber || ""
          ),
          country: values?.country || "canada",
          allowPatientCreationWithAI: values?.allowPatientCreationWithAI
            ? "true"
            : "false",
        };

        if (
          (values?.systemPrompt || "").length > 0 ||
          existingSettings["system-prompt"]
        ) {
          agentSettings["system-prompt"] = await uploadPresignedText(
            orgId,
            serviceLineId,
            "system-prompt",
            values?.systemPrompt || ""
          );
        }
        if (
          (values?.redFlags || "").length > 0 ||
          existingSettings["red-flags"]
        ) {
          agentSettings["red-flags"] = await uploadPresignedText(
            orgId,
            serviceLineId,
            "red-flags",
            values?.redFlags || ""
          );
        }

        const payload = {
          name: values?.name,
          description: values?.description,
          timeZone: values?.timeZone || null,
          intakeQuestionnaire: values?.intakeQuestionnaire,
          dni:
            values?.dni && values.dni.length > 0
              ? values.dni
              : [""],
          resources: formattedResourcesToSave,
          reportDeliveries: cleanedReportDeliveries.length
            ? cleanedReportDeliveries
            : [""],
          "agent-settings": agentSettings,
        };

        const res = await editServiceLine(orgId, serviceLineId, payload);
        if (res?.status === true) {
          success(res?.message);
          navigate("/app/edit-organization", {
            state: {
              userRoles: userRoles,
              modalShow: orgData,
              selectedTab: "Service Lines",
            },
          });
        }
      } catch (error) {
        failed(
          error?.response?.data?.message ||
            error?.message ||
            "Error updating service line"
        );
      } finally {
        setIsPageLoading(false);
      }
    },
  });
  useEffect(() => {
    if (serviceLineId && orgId) {
      setIsPageLoading(true);
      getServiceLineById(orgId, serviceLineId)
        .then(async (res) => {
          if (res?.status === true) {
            setServiceLineData(res?.data);
            const agentSettings = res?.data?.["agent-settings"] || {};
            formik.setFieldValue("name", res?.data?.name);
            formik.setFieldValue("description", res?.data?.description);
            formik.setFieldValue("timeZone", res?.data?.timeZone || null);
            formik.setFieldValue(
              "intakeQuestionnaire",
              res?.data?.intakeQuestionnaire || []
            );
            formik.setFieldValue(
              "resources",
              res?.data?.resources?.map((r) => r.id) || []
            );
            formik.setFieldValue("dni", res?.data?.dni || [""]);
            formik.setFieldValue(
              "humanEscalationNumber",
              formatEscalationNumberForDisplay(
                agentSettings?.humanEscalationNumber ||
                  res?.data?.humanEscalationNumber ||
                  ""
              )
            );
            formik.setFieldValue(
              "reportDeliveries",
              res?.data?.reportDeliveries || [""]
            );
            formik.setFieldValue("country", agentSettings?.country || "canada");
            formik.setFieldValue(
              "allowPatientCreationWithAI",
              String(agentSettings?.allowPatientCreationWithAI || "")
                .toLowerCase()
                .trim() === "true"
            );
            setSelectedResources(res?.data?.resources);

            if (agentSettings?.["system-prompt"]) {
              try {
                const { text } = await fetchPresignedText(
                  orgId,
                  serviceLineId,
                  "system-prompt",
                  "service-line"
                );
                formik.setFieldValue("systemPrompt", text);
              } catch (err) {
                console.warn("Could not load system prompt", err);
              }
            }
            if (agentSettings?.["red-flags"]) {
              try {
                const { text } = await fetchPresignedText(
                  orgId,
                  serviceLineId,
                  "red-flags",
                  "service-line"
                );
                formik.setFieldValue("redFlags", text);
              } catch (err) {
                console.warn("Could not load red flags", err);
              }
            }
          }
        })
        .catch((res) =>
          failed(
            res?.error ||
              res?.message ||
              res?.error?.message ||
              "Error getting Service Line Info"
          )
        )
        .finally(() => setIsPageLoading(false));
    }
  }, [serviceLineId, orgId]);

  useEffect(() => {
    getAvailableQuestionnaires(orgId, "questionnaire", true, true)
      .then((res) => {
        if (res?.status === true) {
          setAvailableQuestionnaires(res?.data);

          const TIQuestionnaires = res.data
            .filter(
              (item) =>
                item?.purpose === "telephone-intake" &&
                item?.status === "active"
            )
            .map((dataItem) => ({
              name: dataItem?.title,
              value: dataItem?.id,
            }));
          // if waiting room is disabled.. allow a N/A or None questionnaire option.
          if(!orgData?.isWaitingRoom) setTelephoneIntakeQuestionnaires([...[{name: "None", value: null}],...TIQuestionnaires])
          else setTelephoneIntakeQuestionnaires(TIQuestionnaires);
        }
      })
      .catch((error) =>
        failed(error?.message || error?.error || "Error getting questionnaires")
      );
  }, [orgId, orgData]);

  useEffect(() => {
    getAuthorizedResources(orgId)
      .then((res) => {
        if (res?.status === true) {
          const transformedData = res?.result
            ?.filter((item) => item?.status === "current")
            ?.map((item) => ({
              ...item,
              name: item?.content?.[0]?.attachment?.title,
              value: item?.id,
            }))
            .sort((a, b) => a.name?.localeCompare(b.name));
          setAvailableResources(transformedData);
        }
      })
      .catch((error) => {
        failed(error?.error || error?.message || "Error getting resources");
      });
  }, [orgId]);

  useEffect(() => {
    getOrganization(orgId).then((res) => {
      if (res?.status === true) {
        setOrgData(res?.data);
        setAvailablaDNIs(res?.data?.ctiSettings?.dni);

        const dniArray = res?.data?.ctiSettings?.dni || [];
        const formattedDNIOptions = dniArray.map((phone) => ({
          name: phone,
          value: phone,
        }));

        setAvailablaDNIs(formattedDNIOptions);
      }
    });
  }, [orgId]);

  useEffect(() => {
    getReportDeliveries(orgId).then((res) => {
      if (res?.status === true) {
        const formattedReportDeliveries = res?.data?.map((item) => ({
          id: item?.id,
          name: item?.name,
          value: item?.id,
        }));
        setAvailableReportDeliveries(formattedReportDeliveries);
      }
    });
  }, [orgId]);

  /* Tabs */
  let tabTitles = [
    "Details",
    "Questionnaires",
    ...(ctiEnabled ? ["Telephony"] : []),
    "Resources",
    "Report Delivery",
    ...(isAdmin ? ["AI Agent"] : []),
  ];

  const tabs = tabTitles.map((title) => ({
    id: title,
    title: title,
    onClick: () => {
      setSelectedTab(title);
    },
    selected: selectedTab === title,
  }));

  const handlePurposeChange = (fieldName, e) => {
    formik.setFieldValue(fieldName, e.target.value);
  };

  const handleRemoveDNI = (index) => {
    const existingValues = formik?.values?.dni || [];
    const newValues = existingValues.filter((_, i) => i !== index);
    formik.setFieldValue("dni", newValues);
  };

  const handleRemoveReportDelivery = (index) => {
    const existingValues = formik?.values?.reportDeliveries || [];
    const newValues = existingValues.filter((_, i) => i !== index);
    formik.setFieldValue("reportDeliveries", newValues);
  };

  const handleAddReportDeliveryRow = () => {
    const existingValues = formik?.values?.reportDeliveries || [];
    formik.setFieldValue("reportDeliveries", [...existingValues, ""]);
  };

  const handleLoadDefaultSystemPrompt = async () => {
    setLoadingDefaultPrompt(true);
    try {
      const { text } = await fetchPresignedText(
        orgId,
        serviceLineId,
        "system-prompt",
        "default"
      );
      success("Default system prompt loaded into editor.");
      return text;
    } catch (error) {
      failed(
        error?.response?.data?.message ||
          error?.message ||
          "Could not load default system prompt"
      );
      return null;
    } finally {
      setLoadingDefaultPrompt(false);
    }
  };

  const handleLoadDefaultRedFlags = async () => {
    setLoadingDefaultRedFlags(true);
    try {
      const { text } = await fetchPresignedText(
        orgId,
        serviceLineId,
        "red-flags",
        "default"
      );
      success("Default red flags loaded into editor.");
      return text;
    } catch (error) {
      failed(
        error?.response?.data?.message ||
          error?.message ||
          "Could not load default red flags"
      );
      return null;
    } finally {
      setLoadingDefaultRedFlags(false);
    }
  };

  const handleAddDNIRow = () => {
    let existingValues = formik?.values?.dni;
    let newValues = [...existingValues, ""];
    formik.setFieldValue("dni", newValues);
  };

  useEffect(() => {
    const checkForSelections = availableResources?.filter((res) =>
      formik?.values?.resources.includes(res.id)
    );
    setSelectedResources(checkForSelections);
  }, [formik.values?.resources, availableResources]);

  const handleLinkOpen = async (url) => {
    const fullUrl = url?.startsWith("http") ? url : `https://${url}`;
    if (fullUrl) window.open(fullUrl, "_blank", "noopener,noreferrer");
  };
  const totalRows = selectedResources?.length;

  return (
    <section className="common-listing">
      {isPageLoading && <FullPageSpinner loadingText="Loading..." />}

      <div>
        <form
          className="common-form border-fields"
          onSubmit={formik.handleSubmit}
        >
          <Row>
            {tabs.map((tab) => (
              <Col
                key={tab.id}
                sm={4}
                md={3}
                lg={2}
                className="mb-3 mb-lg-0"
                onClick={tab.onClick}
              >
                <OrganizationCard title={tab.title} selected={tab.selected} />
              </Col>
            ))}
          </Row>
          <br />
          {orgData?.name ? <h4 style={{color: 'rgb(0, 93, 168)', marginLeft: 0, paddingBottom: 20, whiteSpace: "nowrap", display:"inline-flex", flexDirection:"row", width:"100%"}}><span style={{maxWidth: "50%", overflow:"hidden", textOverflow:"ellipsis"}}>{orgData?.name}</span> - <span style={{maxWidth:"50%", overflow:"hidden", textOverflow:"ellipsis"}}>{formik.values?.name}</span></h4> : null}
          {selectedTab === "Details" && (
            <div>
              <Row>
                <Col>
                  <TextInput
                    type={"text"}
                    keyField={"name"}
                    label={"Display Name"}
                    formik={formik}
                    placeholder={"Display Name"}
                    required={true}
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <TextArea
                    type={"text"}
                    keyField={"description"}
                    label={"Description"}
                    formik={formik}
                    placeholder={"Description"}
                    required={true}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={8} lg={4}>
                  <div className="field-wrap">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <label>Preferred Time Zone (Optional)</label>
                      <Tooltip title="Selecting this on service line will override any settings done at the organization level.">
                        <InfoOutlined style={{ color: "#666", cursor: "pointer" }} />
                      </Tooltip>
                    </div>
                    <Select
                      className="search-select"
                      classNamePrefix="select"
                      isClearable
                      options={timeZoneOptions}
                      filterOption={(option, inputValue) =>
                        option.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      isDisabled={!isAdmin}
                      value={
                        timeZoneOptions.find(
                          (opt) =>
                            opt.value === formik.values.timeZone?.value ||
                            opt.value === formik.values.timeZone?.name
                        ) || null
                      }
                      onChange={(val) => formik.setFieldValue("timeZone", val)}
                      placeholder="Select time zone"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Questionnaires Tab */}
          {selectedTab === "Questionnaires" && (
            <div style={{ marginTop: "30px" }}>
              <Row className="mb-3">
                <Col md={5} lg={4}>
                  <h6 className="text-muted fw-bold">Workflow</h6>
                </Col>
                <Col md={7} lg={8}>
                  <h6 className="text-muted fw-bold">Assign Questionnaire</h6>
                </Col>
              </Row>

              {/* Telephone Intake */}
              <Row className="align-items-center mb-3">
                <Col md={5} lg={4}>
                  <label className="form-label">Intake</label>
                </Col>
                <Col md={7} lg={8}>
                  <SelectFiled
                    keyField={"intakeQuestionnaire"}
                    formik={formik}
                    placeholder={"Select"}
                    options={telephoneIntakeQuestionnaires}
                    onChange={(e) =>
                      handlePurposeChange("intakeQuestionnaire", e)
                    }
                  />
                </Col>
              </Row>
            </div>
          )}

          {/* Telephony Tab */}
          {selectedTab === "Telephony" && (
            <div>
              {formik?.values?.dni?.map((val, index) => (
                <Row key={index} className="align-items-center mb-3">
                  <Col md={5} lg={4}>
                    <label className="form-label fw-bold">{`DNI ${
                      index + 1
                    }`}</label>
                  </Col>
                  <Col md={6} lg={6}>
                    <SelectFiled
                      keyField={`dni[${index}]`}
                      formik={formik}
                      placeholder={"Select phone number"}
                      options={avaialableDNIs}
                      onChange={(e) => handlePurposeChange(`dni[${index}]`, e)}
                    />
                  </Col>
                  <Col xs={1}>
                    <DeleteIcon
                      onClick={() => handleRemoveDNI(index)}
                      style={{
                        cursor: "pointer",
                        color: "#c00",
                        marginBottom: "20px",
                      }}
                      titleAccess="Remove"
                    />
                  </Col>
                </Row>
              ))}
            </div>
          )}

          {/* Resources Tab */}
          {selectedTab === "Resources" && (            <div>
              <div>
                <Row className="align-items-center mb-3">
                  <Col md={5} lg={4}>
                    <label className="form-label">Select Resources</label>
                  </Col>
                  <Col md={7} lg={8}>
                    <MultiSelect
                      required={false}
                      keyField={"resources"}
                      formik={formik}
                      options={availableResources}
                      defaultValue={formik.values.resources}
                    />
                  </Col>
                </Row>
              </div>

              <div className="table-wrap">
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Custodian</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResources?.length > 0 ? (
                      selectedResources
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        ?.map((data) => {
                          let url = data?.content?.[0]?.attachment?.url;

                          return (
                            <tr key={data?.id}>
                              <td>
                                <div
                                  className="click-url"
                                  title={url}
                                  onClick={() => handleLinkOpen(url)}
                                >
                                  {data?.content?.[0]?.attachment?.title}
                                </div>
                              </td>
                              <td>{data?.description}</td>
                              <td>{data?.custodian?.display}</td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <TableCell colSpan="5" style={{ textAlign: "center" }}>
                          No Data Available
                        </TableCell>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {selectedResources?.length > 0 ? (
                <CustomPagination
                  tableData={selectedResources}
                  totalRows={totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  handlePage={(pageNo) => setPage(pageNo)}
                />
              ) : null}
            </div>
          )}

          {/* Report Delivery Tab */}
          {selectedTab === "Report Delivery" && (
            <div>
              {formik?.values?.reportDeliveries?.map((val, index) => (
                <Row key={index} className="align-items-center mb-3">
                  <Col md={5} lg={4}>
                    <label className="form-label fw-bold">{`Report Delivery ${
                      index + 1
                    }`}</label>
                  </Col>
                  <Col md={6} lg={6}>
                    <SelectFiled
                      keyField={`reportDeliveries[${index}]`}
                      formik={formik}
                      placeholder={"Select Report Delivery"}
                      options={availableReportDeliveries}
                      onChange={(e) =>
                        handlePurposeChange(`reportDeliveries[${index}]`, e)
                      }
                    />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center" style={{gap: 10}}>
                    <DeleteIcon
                      onClick={() => handleRemoveReportDelivery(index)}
                      style={{
                        cursor: "pointer",
                        color: "#c00",
                        marginBottom: "2px",
                      }}
                      titleAccess="Remove"
                    />
                  </Col>
                </Row>
              ))}
            </div>
          )}

          {/* AI Agent Tab — Super-Admin only */}
          {isAdmin && selectedTab === "AI Agent" && (
            <div className="ai-agent-settings">
              <Row className="align-items-center mb-3">
                <Col md={5} lg={4} className="d-flex align-items-center">
                  <label className="form-label ai-agent-label">System Prompt</label>
                </Col>
                <Col md={7} lg={8} className="d-flex align-items-center" style={{ gap: 12 }}>
                  <span style={{ color: "#666", fontSize: 14 }}>
                    {(formik.values.systemPrompt || "").trim()
                      ? "Configured — click to view/edit"
                      : "Not configured — click to add"}
                  </span>
                  <Tooltip
                    title={
                      (formik.values.systemPrompt || "").trim()
                        ? "View / edit system prompt"
                        : "Add system prompt"
                    }
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setPromptModal("systemPrompt")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setPromptModal("systemPrompt");
                        }
                      }}
                      style={{ display: "inline-flex", cursor: "pointer" }}
                    >
                      {(formik.values.systemPrompt || "").trim() ? (
                        <VisibilityOutlinedIcon style={{ color: "#005da8" }} />
                      ) : (
                        <EditOutlinedIcon style={{ color: "#005da8" }} />
                      )}
                    </span>
                  </Tooltip>
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={5} lg={4} className="d-flex align-items-center">
                  <label className="form-label ai-agent-label">Red Flags</label>
                </Col>
                <Col md={7} lg={8} className="d-flex align-items-center" style={{ gap: 12 }}>
                  <span style={{ color: "#666", fontSize: 14 }}>
                    {(formik.values.redFlags || "").trim()
                      ? "Configured — click to view/edit"
                      : "Not configured — click to add"}
                  </span>
                  <Tooltip
                    title={
                      (formik.values.redFlags || "").trim()
                        ? "View / edit red flags"
                        : "Add red flags"
                    }
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setPromptModal("redFlags")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setPromptModal("redFlags");
                        }
                      }}
                      style={{ display: "inline-flex", cursor: "pointer" }}
                    >
                      {(formik.values.redFlags || "").trim() ? (
                        <VisibilityOutlinedIcon style={{ color: "#005da8" }} />
                      ) : (
                        <EditOutlinedIcon style={{ color: "#005da8" }} />
                      )}
                    </span>
                  </Tooltip>
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={5} lg={4} className="d-flex align-items-center">
                  <label className="form-label ai-agent-label">
                    Human Escalation Number
                    <Tooltip title="10-digit phone number dialed when the AI transfers a caller to a human team for this service line. +1 is added automatically.">
                      <InfoOutlined
                        style={{
                          color: "#666",
                          cursor: "pointer",
                          fontSize: 20,
                        }}
                      />
                    </Tooltip>
                  </label>
                </Col>
                <Col md={4} lg={3}>
                  <TextInput
                    keyField="humanEscalationNumber"
                    formik={formik}
                    placeholder="Enter here"
                    hideRequired
                  />
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={5} lg={4} className="d-flex align-items-center">
                  <label className="form-label ai-agent-label">
                    Country
                    <Tooltip title="Used by location tools (canada or usa).">
                      <InfoOutlined
                        style={{
                          color: "#666",
                          cursor: "pointer",
                          fontSize: 20,
                        }}
                      />
                    </Tooltip>
                  </label>
                </Col>
                <Col md={4} lg={3}>
                  <SelectFiled
                    keyField="country"
                    formik={formik}
                    placeholder="Select country"
                    options={COUNTRY_OPTIONS}
                    hideRequired
                    onChange={(e) => handlePurposeChange("country", e)}
                  />
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={5} lg={4} className="d-flex align-items-center">
                  <label className="form-label ai-agent-label">
                    Enable patient creation by AI
                    <Tooltip
                      title="If enabled, the AI agent for this service line can create patient accounts."
                      componentsProps={{
                        tooltip: { sx: { fontSize: "1.5rem" } },
                      }}
                    >
                      <InfoOutlined
                        style={{
                          color: "#666",
                          cursor: "pointer",
                          fontSize: 20,
                        }}
                      />
                    </Tooltip>
                  </label>
                </Col>
                <Col md={6} lg={6} className="d-flex align-items-center">
                  <Switch
                    name="allowPatientCreationWithAI"
                    checked={!!formik.values.allowPatientCreationWithAI}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "allowPatientCreationWithAI",
                        e.target.checked
                      )
                    }
                  />
                </Col>
              </Row>

              <AgentPromptEditModal
                show={promptModal === "systemPrompt"}
                onHide={() => setPromptModal(null)}
                title="System Prompt"
                value={formik.values.systemPrompt}
                placeholder="Paste or load the default system prompt, then edit for this service line."
                defaultButtonLabel="Load Default Prompt"
                loadingDefault={loadingDefaultPrompt}
                onLoadDefault={handleLoadDefaultSystemPrompt}
                onApply={(text) => formik.setFieldValue("systemPrompt", text)}
              />
              <AgentPromptEditModal
                show={promptModal === "redFlags"}
                onHide={() => setPromptModal(null)}
                title="Red Flags"
                value={formik.values.redFlags}
                placeholder="Paste or load default red-flag instructions for this service line."
                defaultButtonLabel="Load Default Red Flags"
                loadingDefault={loadingDefaultRedFlags}
                onLoadDefault={handleLoadDefaultRedFlags}
                onApply={(text) => formik.setFieldValue("redFlags", text)}
              />
            </div>
          )}

          <div className="btn-wrap" style={{ display: "flex", gap: "8px" }}>
            <div style={{ marginTop: "20px" }}>
              <Button
                variant="secondary"
                onClick={() => {
                  navigate("/app/edit-organization", {
                    state: {
                      userRoles: userRoles,
                      modalShow: orgData,
                      selectedTab: "Service Lines",
                    },
                  });
                }}
              >
                Cancel
              </Button>
            </div>

            <div style={{ marginTop: "20px" }}>
              <Button type="submit">Update</Button>
            </div>

            {selectedTab === "Report Delivery" && <div className="d-flex align-items-center mb-3" style={{gap: 8, marginTop: "20px", marginLeft: "20px"}}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddReportDeliveryRow} // NEW
                  title="Add Report Delivery row"
                >
                  <AddCircleOutlineIcon style={{ marginRight: 6 }} />
                  Add
                </Button>
            </div>}
              
          </div>
        </form>
      </div>
    </section>
  );
}

export default EditServiceLine;
